import { Box } from "@design-system/Layout/Box";
import { Text } from "@design-system/Typography/Text";
import { type FC, useMemo } from "react";
import { useAiProgressPopupTranslations } from "./i18n";
import { type Question } from "./types";

export const Progress: FC<{
    questions: Question[];
}> = ({ questions }) => {
    const pendingSuggestionCount = useMemo(
        () =>
            questions.filter(
                (question) => question.answer?.aiSuggestion?.isGenerating,
            ).length,
        [questions],
    );

    const numberOfQuestionsLeft = useMemo(
        () => getNumberOfQuestionsLeft(questions),
        [questions],
    );

    const minutesLeft = useMemo(
        () => Math.ceil((numberOfQuestionsLeft * 20) / 60),
        [numberOfQuestionsLeft],
    );

    const { t, keys } = useAiProgressPopupTranslations();

    if (!pendingSuggestionCount) {
        return null;
    }

    return (
        <Box px="4" py="3" className="bg-secondary">
            <Text variant="body-sm" color="secondary">
                {t(keys.progress, {
                    count: minutesLeft,
                })}
            </Text>
        </Box>
    );
};

function getNumberOfQuestionsLeft(questions: Question[]) {
    const questionsLeftPerQuestionnaire = questions.reduce(
        (acc, curr) => {
            if (!curr.questionnaireId) {
                return acc;
            }
            if (!(curr.questionnaireId in acc)) {
                acc[curr.questionnaireId] = 0;
            }

            const { aiSuggestion } = curr.answer ?? {};

            const currentCount = acc[curr.questionnaireId];

            if (aiSuggestion?.isGenerating) {
                acc[curr.questionnaireId] = currentCount + 1;
                return acc;
            }

            if (currentCount > 0 && !aiSuggestion?.success) {
                acc[curr.questionnaireId] = currentCount + 1;
                return acc;
            }

            return acc;
        },
        {} as Record<string, number>,
    );

    const totalQuestionsLeft = Object.values(
        questionsLeftPerQuestionnaire,
    ).reduce((sum, count) => sum + count, 0);

    return totalQuestionsLeft;
}
